import { z } from 'zod';

/**
 * Why a cancel request never reached Stripe. Every one of these was a bare
 * `throw new Error(...)` in the cancel mutation — the church saw a failed
 * modal and we saw nothing, which is the exact silence ADR 0002 exists to
 * remove from a money path.
 */
export const BILLING_CANCEL_FAILURE_REASONS = [
  /** The church row has no Stripe customer — nothing to cancel. */
  'no_customer',
  /** A Stripe customer exists but no active/trialing subscription does. */
  'no_active_subscription',
  /** Stripe rejected the update (schedule conflict, API error). */
  'stripe_error',
  /** Reason was "other" with an empty/whitespace comment — nothing to learn from. */
  'other_reason_missing_comment',
] as const;

export type BillingCancelFailureReason =
  (typeof BILLING_CANCEL_FAILURE_REASONS)[number];

/** Why a churn answer the church actually gave us failed to persist. */
export const BILLING_CHURN_RECORD_FAILURE_REASONS = [
  /** No church on the session — the answer has nothing to attach to. */
  'no_church',
  /** The choice was not in the catalogued list (stale client, hand-crafted call). */
  'invalid_reason',
  /** The insert itself failed. */
  'persist_failed',
] as const;

export type BillingChurnRecordFailureReason =
  (typeof BILLING_CHURN_RECORD_FAILURE_REASONS)[number];

/**
 * Mirrors `CHURN_REASONS` in `@onark/shared/churnReason`, which is the source
 * of truth (the modal renders it, the api stamps it onto Stripe). It is
 * restated here rather than imported because the catalog must stay
 * dependency-free and browser-safe — every other domain module in this package
 * does the same.
 *
 * Drift is caught two ways: adding a reason to the shared list makes the emit
 * call sites stop typechecking (a `ChurnReason` no longer fits this enum), and
 * `apps/api/src/billing/churnReasonCatalog.contract.test.ts` asserts the two
 * lists are identical.
 */
const churnReason = z.enum([
  'too_expensive',
  'switching_tools',
  'missing_features',
  'not_using',
  'paused_ministry',
  'too_complex',
  'other',
]);

const churnSource = z.enum(['self_serve', 'support']);

/**
 * Why a trial-cancel win-back signal did not go out as designed. A skipped
 * `cancel_requested` event is a church that left with no follow-up, and a
 * missing `extend_url` is an email whose button silently disappears — neither
 * shows up anywhere unless it is emitted.
 */
const BILLING_WINBACK_SIGNAL_FAILURE_REASONS = [
  /** The user who clicked cancel has no resolvable email, so nobody to email. */
  'canceler_email_unresolved',
  /** An eligible trial got no extend link (CLASS_LINK_SECRET / API_PUBLIC_URL unset). */
  'extend_link_not_minted',
  /** The Customer.io event or attribute write threw after Stripe accepted. */
  'customerio_signal_failed',
] as const;

/** Why a "two more weeks" click did not extend the trial. */
const BILLING_TRIAL_EXTENSION_REFUSAL_REASONS = [
  /** Forged, expired, or malformed link. */
  'invalid_link',
  /** The subscription is not in a live trial (ended, converted, never one). */
  'not_trialing',
  /** One extension per subscription, and it was already used. */
  'already_extended',
  /** Stripe has no such subscription. */
  'subscription_not_found',
  /** Stripe rejected or failed the read/update. */
  'stripe_error',
] as const;

/**
 * Billing lifecycle — the cancel path and the churn-reason capture hung off it.
 *
 * The measurement this domain exists for is coverage, not volume: `source`
 * says which surface got the answer, and a cancellation that produces a
 * `subscription_cancel_requested` with no matching
 * `cancellation_reason_recorded` is a church that left without telling us why.
 */
export const billingSchemas = {
  /**
   * A church confirmed the cancel modal and Stripe accepted it (cancels at
   * period end). `subscriptionCount` is >1 for legacy customers whose base and
   * usage prices sit on separate subscriptions.
   */
  'billing.subscription_cancel_requested': z.object({
    churchId: z.string(),
    reason: churnReason,
    subscriptionCount: z.number().int().positive(),
  }),
  /**
   * A church still in its Stripe trial changed plan through the deferred
   * (downgrade) path and was switched in place, trial kept. Before this path
   * existed the same click ended the trial and charged the current plan at
   * once, so a charge right after this event is a regression, not a renewal.
   */
  'billing.plan_switched_during_trial': z.object({
    churchId: z.string(),
    pricingId: z.string(),
    /** ISO timestamp of the (unchanged) trial end, when the first charge lands. */
    trialEndsAt: z.string(),
  }),
  /** The cancel never reached Stripe. */
  'billing.subscription_cancel_failed': z.object({
    churchId: z.string().optional(),
    message: z.string().optional(),
    reason: z.enum(BILLING_CANCEL_FAILURE_REASONS),
  }),
  /**
   * A churn reason landed in the database (ActivityEvent
   * `churn.reason_recorded`). `hasDetail` rather than the text itself —
   * free-text churn feedback is customer prose, and PostHog is not where it
   * belongs; the row has it.
   */
  'billing.cancellation_reason_recorded': z.object({
    churchId: z.string(),
    hasDetail: z.boolean(),
    reason: churnReason,
    source: churnSource,
  }),
  /**
   * The church answered and we dropped it. Worth its own event because it is
   * strictly worse than never asking: they spent the goodwill for nothing.
   */
  'billing.cancellation_reason_record_failed': z.object({
    churchId: z.string().optional(),
    message: z.string().optional(),
    reason: z.enum(BILLING_CHURN_RECORD_FAILURE_REASONS),
    source: churnSource,
  }),
  /**
   * Stripe told us a subscription actually ended. `reasonKnown` closes the
   * loop the two events above open: it is false when the subscription carries
   * no `cancellation_reason` metadata — a church that left through the Stripe
   * portal, through support, or through dunning, with the question never asked.
   */
  'billing.subscription_ended': z.object({
    churchId: z.string(),
    reason: churnReason.optional(),
    reasonKnown: z.boolean(),
  }),
  /**
   * A trial cancel was UNDONE (cancel_at_period_end true -> false, from the
   * in-app reactivate or Stripe). Mirror of `subscription_cancel_requested`
   * for trials; drives the Customer.io `cancel_reversed` event.
   */
  'billing.subscription_cancel_reversed': z.object({
    churchId: z.string(),
    subscriptionId: z.string(),
  }),
  /** A trial-cancel win-back signal degraded or was skipped. */
  'billing.winback_signal_failed': z.object({
    /** Absent when the failure came before the church was looked up. */
    churchId: z.string().optional(),
    reason: z.enum(BILLING_WINBACK_SIGNAL_FAILURE_REASONS),
  }),
  /** The one-click link added two weeks to a trial. */
  'billing.trial_extended': z.object({
    churchId: z.string().optional(),
    /** Unix seconds. */
    newTrialEndsAt: z.number().int(),
    subscriptionId: z.string(),
  }),
  /** The one-click link was used and nothing was extended. */
  'billing.trial_extension_refused': z.object({
    message: z.string().optional(),
    reason: z.enum(BILLING_TRIAL_EXTENSION_REFUSAL_REASONS),
    subscriptionId: z.string().optional(),
  }),
};

/**
 * Keep the pre-catalog PostHog insight wired: the billing page has captured a
 * bare `cancelSubscription` since before the catalog existed.
 */
export const BILLING_LEGACY_POSTHOG_NAMES: Readonly<
  Partial<Record<keyof typeof billingSchemas, string>>
> = {
  'billing.subscription_cancel_requested': 'cancelSubscription',
};
